import { useCallback, useEffect, useState } from 'react';
import type { EntryView, LiffSessionView } from '@koto-event/shared';
import { api, ApiError, setIdToken, type LiffConfig } from './lib/api.js';
import { initLiff, openAddFriend, openExternal, reauthenticate } from './lib/liff.js';
import { NoticeScreen, describeError } from './pages/NoticeScreen.js';
import { EventTopScreen } from './pages/EventTopScreen.js';
import { DrawScreen } from './pages/DrawScreen.js';
import { ResultScreen } from './pages/ResultScreen.js';
import { StaffPresentScreen } from './pages/StaffPresentScreen.js';
import { HoneyToastEffect } from './components/HoneyToastEffect.js';

/**
 * LIFF アプリの画面遷移。
 *
 * 提供資料のフロー:
 *   ココデピットにタッチ
 *     → LINEアプリを持っている？（持っていない → 案内画面）
 *     → イベントTOP → 抽選 → 抽選結果（仮当選）
 *     → LINE友だち登録済み？（未登録 → 友だち追加 → 登録を確認）
 *     → 確認・押印（スタッフに提示）→ ハニトー演出 → 特典ゲット
 *     → 「クーポンを見る」（付与先の外部アプリ。本システムは特典を保持しない）
 */
type Screen =
  | { name: 'loading' }
  | { name: 'notice'; title: string; body: string; actionLabel?: string; onAction?: () => void }
  | { name: 'top' }
  | { name: 'draw' }
  | { name: 'result'; entry: EntryView }
  | { name: 'present'; entry: EntryView }
  | { name: 'effect'; entry: EntryView };

export default function App(): JSX.Element {
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const [session, setSession] = useState<LiffSessionView | null>(null);
  const [addFriendUrl, setAddFriendUrl] = useState('');
  const [rewardViewUrl, setRewardViewUrl] = useState('');
  /** 電子スタンプが使える環境か（未設定の環境では押印枠を出さず、開発環境は管理画面から手動確定する） */
  const [digishotEnabled, setDigishotEnabled] = useState(false);

  // --- 起動処理 -------------------------------------------------------------
  useEffect(() => {
    let active = true;
    void (async () => {
      const params = new URLSearchParams(window.location.search);

      // CocodePit 着地でエラーになった場合は、そのまま案内画面へ
      const errorCode = params.get('error');
      if (errorCode) {
        setScreen({ name: 'notice', ...describeError(errorCode) });
        return;
      }

      let config: LiffConfig;
      try {
        config = await api.config();
      } catch {
        if (active) setScreen({
          name: 'notice',
          title: 'イベントを読み込めませんでした',
          body: '通信状況を確認して、もう一度読み込んでください。',
          actionLabel: 'もう一度読み込む',
          onAction: () => window.location.reload(),
        });
        return;
      }
      if (!active) return;
      setAddFriendUrl(config.addFriendUrl);
      setRewardViewUrl(config.rewardViewUrl);
      setDigishotEnabled(config.digishotEnabled);

      // LIFF の起動パラメータは liff.state 経由で入ることがあるため両方見る。値は CocodePit タッチの ID
      const touchId = params.get('es') ?? new URLSearchParams(params.get('liff.state') ?? '').get('es');

      const liffState = await initLiff(config.liffId);
      if (!active) return;
      if (!liffState.ready) return; // ログインへリダイレクト中

      // LINE アプリ外からの起動（フロー図「LINEアプリを持っている？→持っていない」）。
      // 端末によっては CocodePit から LINE が自動で開かないため、明示的に LINE で開き直す操作を用意する
      // （実現性検討メモ R1）。タッチ ID を引き継ぐので、入口の有効期限内なら抽選前の状態から再開できる。
      if (liffState.error || !liffState.idToken) {
        const reopenUrl = config.liffId
          ? `https://liff.line.me/${config.liffId}${touchId ? `?es=${encodeURIComponent(touchId)}` : ''}`
          : null;
        setScreen({
          name: 'notice',
          title: 'LINEの起動を確認できませんでした',
          body: reopenUrl
            ? '通信状況を確認して、下のボタンからLINEで開き直してください。' +
              'LINEアプリをお持ちでない場合は、インストール後にもう一度ココデピットにタッチしてください。'
            : 'LINEアプリをインストールして、もう一度ココデピットにタッチしてください。',
          ...(reopenUrl ? { actionLabel: 'LINEで開く', onAction: () => window.location.assign(reopenUrl) } : {}),
        });
        return;
      }

      setIdToken(liffState.idToken);

      if (!touchId) {
        // タッチを経由していない直接アクセス
        setScreen({ name: 'notice', ...describeError('NO_SESSION') });
        return;
      }

      try {
        const result = await api.startSession(touchId);
        if (!active) return;
        setSession(result);

        // 仮当選・確定後の付与結果を再開する。確定済みには友だち登録や押印をもう一度要求しない。
        if (result.pendingEntry) {
          const pending = result.pendingEntry;
          if (pending.status === 'NO_PRIZE') {
            setScreen({ name: 'result', entry: pending });
            return;
          }
          if (pending.status === 'EXPIRED') {
            setScreen({ name: 'notice', ...describeError('ENTRY_EXPIRED') });
            return;
          }
          const needsFriend = pending.status === 'PROVISIONAL' && result.event.requireFriendForReward && !result.isFriend;
          setScreen(needsFriend ? { name: 'result', entry: pending } : { name: 'present', entry: pending });
          return;
        }

        if (!result.canDraw && result.blockedReason) {
          setScreen({ name: 'notice', ...describeError(result.blockedReason) });
          return;
        }

        setScreen({ name: 'top' });
      } catch (e) {
        if (!active) return;
        const code = e instanceof Error && 'code' in e ? String((e as { code: string }).code) : 'UNKNOWN';
        setScreen(e instanceof ApiError && (e.status === 0 || e.status === 401 || e.status >= 500)
          ? {
            name: 'notice',
            title: e.status === 401 ? 'LINEの認証を確認してください' : '通信を確認してください',
            body: 'もう一度読み込むと、このイベントの続きから確認できます。',
            actionLabel: e.status === 401 ? 'LINE認証をやり直す' : 'もう一度読み込む',
            onAction: e.status === 401 ? reauthenticate : () => window.location.reload(),
          }
          : { name: 'notice', ...describeError(code) });
      }
    })();
    return () => { active = false; };
  }, []);

  // --- ハンドラ -------------------------------------------------------------
  const handleDraw = useCallback(async (): Promise<EntryView> => {
    if (!session) throw new Error('セッションがありません');
    return api.draw(session.touchId);
  }, [session]);

  const handlePoll = useCallback(async (): Promise<EntryView> => {
    if (screen.name !== 'present') throw new Error('不正な状態です');
    return api.getEntry(screen.entry.id);
  }, [screen]);

  const handleCheckFriend = useCallback(async (): Promise<EntryView> => {
    if (screen.name !== 'result') throw new Error('不正な状態です');
    return api.checkFriend(screen.entry.id);
  }, [screen]);

  // --- 描画 -----------------------------------------------------------------
  if (screen.name === 'loading') {
    return (
      <div className="app">
        <p className="muted center">読み込み中…</p>
      </div>
    );
  }

  return (
    <div className="app">
      {screen.name === 'notice' && (
        <NoticeScreen
          title={screen.title}
          body={screen.body}
          actionLabel={screen.actionLabel}
          onAction={screen.onAction}
        />
      )}

      {screen.name === 'top' && session && (
        <EventTopScreen event={session.event} onStart={() => setScreen({ name: 'draw' })} />
      )}

      {screen.name === 'draw' && session && (
        <DrawScreen
          event={session.event}
          onDraw={handleDraw}
          onComplete={(entry) => setScreen({ name: 'result', entry })}
          onError={(message, authenticationExpired) => setScreen({
            name: 'notice', title: '抽選できませんでした', body: message,
            ...(authenticationExpired ? { actionLabel: 'LINE認証をやり直す', onAction: reauthenticate } : {}),
          })}
        />
      )}

      {screen.name === 'result' && session && (
        <ResultScreen
          event={session.event}
          entry={screen.entry}
          addFriendUrl={addFriendUrl}
          onAddFriend={() => openAddFriend(addFriendUrl)}
          onCheckFriend={handleCheckFriend}
          onProceed={() => setScreen({ name: 'present', entry: screen.entry })}
          onUpdated={(entry) => {
            if (entry.isFriend) setScreen({ name: 'present', entry });
            else setScreen({ name: 'result', entry });
          }}
        />
      )}

      {screen.name === 'present' && session && (
        <StaffPresentScreen
          event={session.event}
          entry={screen.entry}
          digishotEnabled={digishotEnabled}
          onPoll={handlePoll}
          onStamped={(entry) => setScreen({ name: 'effect', entry })}
          onExpired={() => setScreen({ name: 'notice', ...describeError('ENTRY_EXPIRED') })}
        />
      )}

      {screen.name === 'effect' && (
        <HoneyToastEffect
          prizeName={screen.entry.prize?.name ?? '特典'}
          imageUrl={session?.event.screens.stampEffectImageUrl}
          body={session?.event.screens.stampEffectBody}
          onFinished={() => {
            // 押印で確定しても外部システムへの付与に失敗している、または付与待ちが長く続いている場合がある（管理画面から再送して回復する）
            if (screen.entry.rewardGrantStatus === 'FAILED' || screen.entry.rewardGrantStatus === 'PENDING') {
              const receipt = screen.entry.id.slice(0, 8).toUpperCase();
              setScreen({
                name: 'notice',
                title: '特典の付与を確認しています',
                body: `特典の付与に時間がかかっています。お手数ですがスタッフにお声がけください（受付番号: ${receipt}）。`,
              });
              return;
            }
            setScreen({
              name: 'notice',
              title: '特典を受け取りました',
              body: rewardViewUrl
                ? '付与された特典は、下のボタンから確認できます。'
                : '付与された特典は、公式LINEのメニューから確認できます。',
              ...(rewardViewUrl ? { actionLabel: 'クーポンを見る', onAction: () => openExternal(rewardViewUrl) } : {}),
            });
          }}
        />
      )}
    </div>
  );
}
